/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { Loader2, Upload, Search, X, Trash2 } from "lucide-react";

const DOC_PAGE_SIZE = 10;

const resolveFileLink = (file) => {
    if (file?.objectKey) return "__objectKey__";
    return file?.signedUrl || file?.downloadUrl || file?.previewUrl || null;
};

const DocumentsWidget = ({
    caseId,
    caseFiles = {},
    isUploading,
    uploadMessage,
    uploadProgress,
    isCaseHydrating,
    onDocumentUpload,
    handlePreview,
    onDeleteFile,
    isDeletingFile,
    fileDeleteTarget
}) => {
    const [documentSearchTerm, setDocumentSearchTerm] = useState("");
    const [docPage, setDocPage] = useState(1);

    const currentFiles = caseFiles[caseId] || [];

    const filteredFiles = currentFiles.filter((file) => {
        if (!documentSearchTerm.trim()) return true;
        const term = documentSearchTerm.toLowerCase();
        return (
            (file.name || "").toLowerCase().includes(term) ||
            (file.source || "").toLowerCase().includes(term)
        );
    });

    const displayFiles = filteredFiles.slice(0, docPage * DOC_PAGE_SIZE);
    const hasMore = filteredFiles.length > docPage * DOC_PAGE_SIZE;

    return (
        <div className="border dark:border-slate-800 border-slate-200 rounded-xl p-5 dark:bg-slate-900/70 bg-white shadow-sm">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-sm font-semibold dark:text-white text-slate-900">Upload documents</p>
                    <p className="text-xs dark:text-slate-500 text-slate-500">Attach credit, income, or collateral docs</p>
                </div>
                <label
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 dark:text-slate-200 text-slate-700 cursor-pointer dark:hover:bg-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-60"
                    aria-disabled={isUploading}
                >
                    {isUploading ? <Loader2 className="w-4 h-4 animate-spin text-yellow-400" /> : <Upload className="w-4 h-4" />}
                    <span className="text-sm font-semibold">{isUploading ? "Uploading..." : "Add Files"}</span>
                    <input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xls,.xlsx"
                        onChange={onDocumentUpload}
                        className="hidden"
                        disabled={isUploading}
                    />
                </label>
            </div>

            {uploadMessage && <p className="dark:text-yellow-400 text-yellow-600 text-sm mb-3">{uploadMessage}</p>}
            {isUploading && (
                <p className="text-slate-500 text-xs mb-2">
                    Saving files... {uploadProgress ? `${uploadProgress}%` : ""}
                </p>
            )}

            <div className="dark:bg-slate-950 bg-slate-50 border dark:border-slate-800 border-slate-200 rounded-lg p-4 max-h-72 overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Document Library</p>
                    {isCaseHydrating && (
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Loader2 className="w-3 h-3 animate-spin text-yellow-400" />
                            <span>Refreshing...</span>
                        </div>
                    )}
                    <span className="text-xs text-slate-400">
                        {currentFiles.length} file{currentFiles.length === 1 ? "" : "s"}
                    </span>
                </div>

                {/* Search */}
                <div className="mb-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                            type="text"
                            placeholder="Search documents..."
                            value={documentSearchTerm}
                            onChange={(e) => setDocumentSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-200 rounded-lg text-sm dark:text-slate-200 text-slate-900 dark:placeholder-slate-500 placeholder-slate-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                        />
                        {documentSearchTerm && (
                            <button
                                onClick={() => setDocumentSearchTerm("")}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                {currentFiles.length > 0 ? (
                    <>
                        <ul className="space-y-2">
                            {displayFiles.map((file, index) => (
                                <li
                                    key={`${file.name}-${file.uploadedAt || index}`}
                                    className="flex items-center gap-3 justify-between text-sm dark:text-slate-200 text-slate-700 dark:bg-slate-900 bg-white rounded-lg px-3 py-2 border dark:border-slate-800 border-slate-200"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="truncate font-semibold">{file.name}</p>
                                        <p className="text-xs text-slate-500">
                                            {file.source || "Uploaded"}
                                            {file.uploadedAt ? ` • ${new Date(file.uploadedAt).toLocaleString()}` : ""}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {(file.objectKey || resolveFileLink(file)) ? (
                                            <button
                                                onClick={() => {
                                                    const raw = resolveFileLink(file);
                                                    const link = raw === "__objectKey__" ? null : raw;
                                                    handlePreview({
                                                        ...file,
                                                        previewUrl: link,
                                                        url: link,
                                                        type: file.type || file.contentType || "application/pdf"
                                                    });
                                                }}
                                                className="px-3 py-1 rounded-lg border dark:border-yellow-500/40 border-yellow-400/40 dark:text-yellow-300 text-yellow-700 hover:text-yellow-800 dark:hover:text-yellow-200 dark:hover:border-yellow-400 hover:bg-yellow-50 dark:hover:bg-transparent transition-colors text-xs font-semibold"
                                                disabled={isCaseHydrating}
                                            >
                                                {isCaseHydrating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}
                                            </button>
                                        ) : (
                                            <span className="text-xs text-slate-500">No preview available</span>
                                        )}
                                        <button
                                            type="button"
                                            className="p-2 rounded-lg dark:text-slate-400 text-slate-500 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-500/10 transition-colors"
                                            onClick={() => onDeleteFile && onDeleteFile(file)}
                                            disabled={isDeletingFile && fileDeleteTarget === file.name}
                                        >
                                            {isDeletingFile && fileDeleteTarget === file.name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        {hasMore && (
                            <div className="mt-3 flex justify-center">
                                <button
                                    onClick={() => setDocPage((prev) => prev + 1)}
                                    className="px-3 py-1 rounded-lg border dark:border-slate-700 border-slate-300 dark:text-slate-200 text-slate-600 text-xs hover:border-yellow-500 dark:hover:border-yellow-500 transition-colors"
                                >
                                    Show more files
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <p className="text-slate-500 text-sm">No documents uploaded yet.</p>
                )}
            </div>
        </div>
    );
};

export default DocumentsWidget;
